import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
  Typography,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import image from './image.jpeg'; // Adjust the path as necessary
import { useParams } from 'react-router-dom';
import appointmentService from '../../services/http/appointments/appointment.service';

const PatientPaymentForm = () => {
  const { apptRequestId } = useParams<{ apptRequestId: string }>();
  console.log('Appointment Request ID:', apptRequestId);

  const decodedId = apptRequestId
    ? atob(apptRequestId.replace(/[^A-Za-z0-9+/=]/g, ''))
    : '';
  console.log('Decoded Appointment Request ID:', decodedId);
  const [formData, setFormData] = useState({
    fullName: '',
    dob: '',
    email: '',
    phone: '',
    gender: '',
    weight: '',
    consultationType: 'fresh',
  });

  const [phoneError, setPhoneError] = useState('');
  const [practitionerName, setPractitionerName] = useState('');
  const [amount, setAmount] = useState<string>('0');
  const [paymentLink, setPaymentLink] = useState('');
  // const [loading, setLoading] = useState(false);
  const [linkExpired, setLinkExpired] = useState(false);


  useEffect(() => {
    if (!decodedId) return;

    async function fetchPaymentData() {
      const response = await appointmentService.getPaymentFormData(decodedId!, {});

      if (response.linkExpired) {
        setLinkExpired(true);
        return;
      }

      // ✅ Correctly access nested data
      const details = response.data || {};
      setPractitionerName(details.practitionerName || '');
      setAmount(details.amount || '');
      if (details.paymentLink) {
        setPaymentLink(details.paymentLink);
      }
    }

    fetchPaymentData();
  }, [decodedId]);

  if (!apptRequestId) {
    console.error('No appointment request ID found in URL');
    return null; // ya navigate to error page
  }


  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent
  ) => {
    const { name, value } = e.target;

    // Phone: keep digits only (drops spaces the keyboard/autofill adds) and cap at 10
    if (name === 'phone') {
      const digits = value.replace(/\D/g, '').slice(0, 10);
      setPhoneError(
        digits.length > 0 && digits.length < 10
          ? 'Phone number must be exactly 10 digits'
          : ''
      );
      setFormData({
        ...formData,
        phone: digits,
      });
      return;
    }

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // const handleSubmit = async (e:any) => {
  //     e.preventDefault();
  //   // setLoading(true)
  //       if (paymentLink) {
  //         window.open(paymentLink, '_blank');
  //         // setLoading(false);
  //         return;
  //       }
  // };
  const handleSubmit = async (e: any) => {
    e.preventDefault();

    if (!/^\d{10}$/.test(formData.phone)) {
      setPhoneError('Phone number must be exactly 10 digits');
      return;
    }

    let link = paymentLink;
    if (!paymentLink) {
      const response = await appointmentService.getPaymentFormData(decodedId!, { patientData: formData });
      if (response.linkExpired) {
        setLinkExpired(true);
        return;
      }
      link = response.data.paymentLink;
    }

    if (link) {
      window.location.href = link;
    }
  };



  if (linkExpired) {
    return (
      <Container maxWidth="sm" style={{ marginTop: '2rem', textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          Payment link has expired. Please contact support.
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box mt={4} display="flex" flexDirection="column" alignItems="center">
        {/* Logo */}
        <Box mb={2}>
          <img
            src={image}
            alt="Schedula"
            style={{ height: 50 }}
          />
        </Box>

        {/* Welcome Heading */}
        <Typography variant="h6" align="center" gutterBottom>
          Welcome to Dr.{practitionerName}'s Regular Consultation
        </Typography>

        {/* Subheading */}
        <Typography
          align="center"
          sx={{ color: '#3492C3', fontSize: 20, fontWeight: 700, mb: 2 }}
        >
          Kindly confirm your details
        </Typography>

        {/* Form */}
        <Box
          component="form"
          onSubmit={handleSubmit}
          p={3}
          boxShadow={3}
          borderRadius={2}
          width="100%"
        >
          <TextField
            fullWidth
            required
            label="Full Name"
            name="fullName"
            value={formData.fullName}
            onChange={handleChange}
            margin="normal"
          />
          <TextField
            fullWidth
            required
            label="Date of Birth"
            name="dob"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={formData.dob}
            onChange={handleChange}
            margin="normal"
          />
          <TextField
            fullWidth
            required
            label="Enter Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            margin="normal"
          />
          <TextField
            fullWidth
            required
            label="Phone Number"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleChange}
            error={Boolean(phoneError)}
            helperText={phoneError}
            inputProps={{ inputMode: 'numeric', maxLength: 10, pattern: '[0-9]{10}' }}
            margin="normal"
          />
          <FormControl fullWidth required margin="normal">
            <InputLabel>Gender</InputLabel>
            <Select
              name="gender"
              value={formData.gender}
              onChange={handleChange}
              label="Gender"
            >
              <MenuItem value="male">Male</MenuItem>
              <MenuItem value="female">Female</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Approximate Weight (kg)"
            name="weight"
            value={formData.weight}
            onChange={handleChange}
            margin="normal"
          />
          <FormControl component="fieldset" margin="normal">
            <Typography>Consultation Type</Typography>
            <RadioGroup
              row
              name="consultationType"
              value={formData.consultationType}
              onChange={handleChange}
            >
              <FormControlLabel value="fresh" control={<Radio />} label="Fresh Consultation" />
              <FormControlLabel value="review" control={<Radio />} label="Review/Follow Up" />
            </RadioGroup>
          </FormControl>
          <Box mt={3}>
            <Button type="submit" variant="contained" color="primary" fullWidth>
              Confirm & Pay {amount}
            </Button>
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default PatientPaymentForm;